import type { SupportBugInvestigationDebugContextPayload } from "../shared/supportConversationsTypes";
import { sanitizeSupportNetworkResourceUrl } from "./sanitizeSupportPageUrl";

const MAX_CONSOLE_HISTORY_ENTRIES = 50;
const MAX_NETWORK_HISTORY_ENTRIES = 100;
const MAX_CONSOLE_MESSAGE_CHARACTERS = 2_000;
const MAX_SERIALIZED_ARGUMENT_CHARACTERS = 4_000;
const MAX_STACK_CHARACTERS = 4_000;
const MAX_NETWORK_ERROR_CHARACTERS = 1_000;
const SUPPORT_DIAGNOSTIC_STATE_PROPERTY = "__jungleSupportBrowserDiagnosticHistoryV1";
const CAPTURED_CONSOLE_LEVELS = ["warn", "error"] as const;

type SupportConsoleEntry = SupportBugInvestigationDebugContextPayload["console_entries"][number];
type SupportNetworkEntry = SupportBugInvestigationDebugContextPayload["network_entries"][number];
type CapturedConsoleLevel = (typeof CAPTURED_CONSOLE_LEVELS)[number];
type ConsoleMethod = (...argumentsToLog: unknown[]) => void;

type InstrumentedXmlHttpRequestState = {
  method: string;
  rawUrl: string;
  startedAtIso: string | null;
  startedAtMonotonicMilliseconds: number | null;
  errorKind: string | null;
  hasRecordedCompletion: boolean;
  hasInstalledListeners: boolean;
};

type SupportBrowserDiagnosticState = {
  isInstalled: boolean;
  nextEntrySequence: number;
  consoleEntries: SupportConsoleEntry[];
  networkEntries: SupportNetworkEntry[];
  originalConsoleMethods: Partial<Record<CapturedConsoleLevel, ConsoleMethod>>;
  patchedConsoleMethods: Partial<Record<CapturedConsoleLevel, ConsoleMethod>>;
  originalFetch: typeof window.fetch | null;
  patchedFetch: typeof window.fetch | null;
  originalXmlHttpRequestOpen: XMLHttpRequest["open"] | null;
  originalXmlHttpRequestSend: XMLHttpRequest["send"] | null;
  patchedXmlHttpRequestOpen: XMLHttpRequest["open"] | null;
  patchedXmlHttpRequestSend: XMLHttpRequest["send"] | null;
  xmlHttpRequestStateByRequest: WeakMap<XMLHttpRequest, InstrumentedXmlHttpRequestState>;
  windowErrorListener: ((event: ErrorEvent) => void) | null;
  unhandledRejectionListener: ((event: PromiseRejectionEvent) => void) | null;
};

export type SupportBrowserDiagnosticHistorySnapshot = {
  consoleCaptureActive: boolean;
  networkCaptureActive: boolean;
  consoleEntries: SupportConsoleEntry[];
  networkEntries: SupportNetworkEntry[];
};

function createEmptyDiagnosticState(): SupportBrowserDiagnosticState {
  return {
    isInstalled: false,
    nextEntrySequence: 0,
    consoleEntries: [],
    networkEntries: [],
    originalConsoleMethods: {},
    patchedConsoleMethods: {},
    originalFetch: null,
    patchedFetch: null,
    originalXmlHttpRequestOpen: null,
    originalXmlHttpRequestSend: null,
    patchedXmlHttpRequestOpen: null,
    patchedXmlHttpRequestSend: null,
    xmlHttpRequestStateByRequest: new WeakMap(),
    windowErrorListener: null,
    unhandledRejectionListener: null,
  };
}

function readDiagnosticState(): SupportBrowserDiagnosticState | null {
  if (typeof window === "undefined") return null;
  const windowRecord = window as unknown as Record<string, unknown>;
  const candidateState = windowRecord[SUPPORT_DIAGNOSTIC_STATE_PROPERTY];
  return candidateState && typeof candidateState === "object"
    ? (candidateState as SupportBrowserDiagnosticState)
    : null;
}

function getOrCreateDiagnosticState(): SupportBrowserDiagnosticState | null {
  if (typeof window === "undefined") return null;
  const existingState = readDiagnosticState();
  if (existingState) return existingState;
  const createdState = createEmptyDiagnosticState();
  (window as unknown as Record<string, unknown>)[SUPPORT_DIAGNOSTIC_STATE_PROPERTY] = createdState;
  return createdState;
}

function appendBoundedEntry<EntryType>(entries: EntryType[], entry: EntryType, maximumEntryCount: number) {
  entries.push(entry);
  if (entries.length > maximumEntryCount) {
    entries.splice(0, entries.length - maximumEntryCount);
  }
}

function nextEntryId(state: SupportBrowserDiagnosticState, prefix: string): string {
  state.nextEntrySequence += 1;
  return `${prefix}-${state.nextEntrySequence}`;
}

function sanitizeUrlInsideDiagnosticText(rawUrl: string): string {
  const trailingPunctuationMatch = rawUrl.match(/[),.;]+$/);
  const trailingPunctuation = trailingPunctuationMatch?.[0] ?? "";
  const urlWithoutTrailingPunctuation = trailingPunctuation
    ? rawUrl.slice(0, -trailingPunctuation.length)
    : rawUrl;
  const sanitizedUrl = sanitizeSupportNetworkResourceUrl(urlWithoutTrailingPunctuation);
  return `${sanitizedUrl ?? "[REDACTED_URL]"}${trailingPunctuation}`;
}

/**
 * Browser logs are attacker- and customer-controlled. Keep useful error text
 * while removing the common credential/contact shapes before anything reaches
 * the in-memory ring. The Worker repeats secret redaction before S3 storage.
 */
export function sanitizeSupportBrowserDiagnosticText(rawValue: unknown, maximumCharacters: number): string {
  const rawText = typeof rawValue === "string" ? rawValue : String(rawValue ?? "");
  return rawText
    .replace(/\bBearer\s+[^\s,;]+/gi, "Bearer [REDACTED]")
    .replace(/\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, "[REDACTED_TOKEN]")
    .replace(
      /\b(api[-_]?key|token|secret|password|authorization|credential|session(?:id)?|sid)\b(\s*[:=]\s*)["']?[^\s,;&}"']+/gi,
      "$1$2[REDACTED]"
    )
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[REDACTED_EMAIL]")
    .replace(/https?:\/\/[^\s<>"']+/gi, sanitizeUrlInsideDiagnosticText)
    .slice(0, maximumCharacters);
}

function describeDiagnosticArgument(argumentValue: unknown): string {
  if (argumentValue === null) return "null";
  if (argumentValue === undefined) return "undefined";
  if (typeof argumentValue === "string") {
    return sanitizeSupportBrowserDiagnosticText(argumentValue, MAX_CONSOLE_MESSAGE_CHARACTERS);
  }
  if (
    typeof argumentValue === "number" ||
    typeof argumentValue === "boolean" ||
    typeof argumentValue === "bigint"
  ) {
    return String(argumentValue);
  }
  if (argumentValue instanceof Error) {
    return sanitizeSupportBrowserDiagnosticText(
      `${argumentValue.name || "Error"}: ${argumentValue.message}`,
      MAX_CONSOLE_MESSAGE_CHARACTERS
    );
  }
  if (typeof URL !== "undefined" && argumentValue instanceof URL) {
    return sanitizeSupportNetworkResourceUrl(argumentValue.toString()) ?? "[URL]";
  }
  if (typeof Request !== "undefined" && argumentValue instanceof Request) {
    const requestUrl = sanitizeSupportNetworkResourceUrl(argumentValue.url) ?? "[URL]";
    return `[Request ${normalizeHttpMethod(argumentValue.method)} ${requestUrl}]`;
  }
  if (typeof Response !== "undefined" && argumentValue instanceof Response) {
    return `[Response ${argumentValue.status || "status unknown"}]`;
  }
  if (Array.isArray(argumentValue)) {
    return `[Array(${argumentValue.length})]`;
  }
  if (typeof argumentValue === "function") {
    return `[Function ${argumentValue.name || "anonymous"}]`;
  }
  if (typeof argumentValue === "symbol") return "[Symbol]";
  if (typeof argumentValue === "object") {
    let objectKeys: string[] = [];
    try {
      objectKeys = Object.keys(argumentValue as Record<string, unknown>).slice(0, 8);
    } catch {
      return "[Object]";
    }
    const sanitizedKeys = objectKeys.map((objectKey) =>
      sanitizeSupportBrowserDiagnosticText(objectKey, 100)
    );
    return sanitizedKeys.length > 0 ? `[Object keys: ${sanitizedKeys.join(", ")}]` : "[Object]";
  }
  return `[${typeof argumentValue}]`;
}

function findFirstError(argumentsToLog: unknown[]): Error | null {
  return argumentsToLog.find((argumentValue): argumentValue is Error => argumentValue instanceof Error) ?? null;
}

function recordConsoleEntry(
  state: SupportBrowserDiagnosticState,
  level: CapturedConsoleLevel,
  argumentsToLog: unknown[],
  messagePrefix = ""
) {
  const describedArguments = argumentsToLog.map(describeDiagnosticArgument);
  const joinedMessage = [messagePrefix, ...describedArguments].filter(Boolean).join(" ");
  const firstError = findFirstError(argumentsToLog);
  appendBoundedEntry(
    state.consoleEntries,
    {
      id: nextEntryId(state, "console"),
      timestamp: new Date().toISOString(),
      level,
      message: sanitizeSupportBrowserDiagnosticText(joinedMessage, MAX_CONSOLE_MESSAGE_CHARACTERS),
      serialized_arguments: sanitizeSupportBrowserDiagnosticText(
        JSON.stringify(describedArguments),
        MAX_SERIALIZED_ARGUMENT_CHARACTERS
      ),
      stack: firstError?.stack
        ? sanitizeSupportBrowserDiagnosticText(firstError.stack, MAX_STACK_CHARACTERS)
        : undefined,
    },
    MAX_CONSOLE_HISTORY_ENTRIES
  );
}

function normalizeHttpMethod(rawMethod: unknown): string {
  if (typeof rawMethod !== "string") return "UNKNOWN";
  const normalizedMethod = rawMethod.trim().toUpperCase();
  return /^[A-Z]{1,20}$/.test(normalizedMethod) ? normalizedMethod : "UNKNOWN";
}

function resolveSanitizedRequestUrl(rawUrl: unknown): string | null {
  if (typeof window === "undefined") return null;
  try {
    const absoluteUrl = new URL(String(rawUrl ?? ""), window.location.href).toString();
    return sanitizeSupportNetworkResourceUrl(absoluteUrl);
  } catch {
    return null;
  }
}

function finiteResponseStatusOrNull(rawStatus: unknown): number | null {
  return typeof rawStatus === "number" && Number.isFinite(rawStatus) && rawStatus > 0
    ? Math.round(rawStatus)
    : null;
}

function monotonicNowMilliseconds(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

function recordNetworkEntry(
  state: SupportBrowserDiagnosticState,
  entry: Omit<SupportNetworkEntry, "id" | "request_headers" | "request_body" | "response_headers" | "response_body">
) {
  if (!entry.url) return;
  appendBoundedEntry(
    state.networkEntries,
    {
      ...entry,
      id: nextEntryId(state, "request"),
      request_headers: {},
      request_body: null,
      response_headers: {},
      response_body: null,
    },
    MAX_NETWORK_HISTORY_ENTRIES
  );
}

function installConsoleCapture(state: SupportBrowserDiagnosticState) {
  if (typeof console === "undefined") return;
  for (const consoleLevel of CAPTURED_CONSOLE_LEVELS) {
    const originalConsoleMethod = console[consoleLevel] as ConsoleMethod | undefined;
    if (typeof originalConsoleMethod !== "function") continue;
    const patchedConsoleMethod: ConsoleMethod = (...argumentsToLog) => {
      try {
        recordConsoleEntry(state, consoleLevel, argumentsToLog);
      } catch {
        // Diagnostics must never prevent the application from logging.
      }
      originalConsoleMethod.apply(console, argumentsToLog);
    };
    try {
      state.originalConsoleMethods[consoleLevel] = originalConsoleMethod;
      state.patchedConsoleMethods[consoleLevel] = patchedConsoleMethod;
      console[consoleLevel] = patchedConsoleMethod;
    } catch {
      delete state.originalConsoleMethods[consoleLevel];
      delete state.patchedConsoleMethods[consoleLevel];
    }
  }
}

function readFetchRequestDetails(input: RequestInfo | URL, init?: RequestInit) {
  const requestMethod =
    init?.method ?? (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET");
  const rawRequestUrl =
    typeof input === "string" || (typeof URL !== "undefined" && input instanceof URL)
      ? input.toString()
      : typeof Request !== "undefined" && input instanceof Request
        ? input.url
        : "";
  return {
    method: normalizeHttpMethod(requestMethod),
    url: resolveSanitizedRequestUrl(rawRequestUrl),
  };
}

function installFetchCapture(state: SupportBrowserDiagnosticState) {
  if (typeof window.fetch !== "function") return;
  const originalFetch = window.fetch;
  const patchedFetch: typeof window.fetch = async (input, init) => {
    const requestStartedAtIso = new Date().toISOString();
    const requestStartedAtMonotonicMilliseconds = monotonicNowMilliseconds();
    let requestDetails: ReturnType<typeof readFetchRequestDetails> = {
      method: "UNKNOWN",
      url: null,
    };
    try {
      requestDetails = readFetchRequestDetails(input, init);
    } catch {
      // Preserve fetch behavior even if an unusual Request object cannot be inspected.
    }
    try {
      const response = await originalFetch.call(window, input, init);
      if (requestDetails.url) {
        try {
          recordNetworkEntry(state, {
            timestamp: requestStartedAtIso,
            method: requestDetails.method,
            url: requestDetails.url,
            status: finiteResponseStatusOrNull(response.status),
            duration_ms: Math.max(
              0,
              Math.round(monotonicNowMilliseconds() - requestStartedAtMonotonicMilliseconds)
            ),
            error_message: null,
          });
        } catch {
          // Diagnostics must never replace a successful response.
        }
      }
      return response;
    } catch (requestError) {
      if (requestDetails.url) {
        try {
          recordNetworkEntry(state, {
            timestamp: requestStartedAtIso,
            method: requestDetails.method,
            url: requestDetails.url,
            status: null,
            duration_ms: Math.max(
              0,
              Math.round(monotonicNowMilliseconds() - requestStartedAtMonotonicMilliseconds)
            ),
            error_message: sanitizeSupportBrowserDiagnosticText(
              requestError instanceof Error
                ? `${requestError.name}: ${requestError.message}`
                : String(requestError),
              MAX_NETWORK_ERROR_CHARACTERS
            ),
          });
        } catch {
          // Preserve the original rejection when diagnostics cannot be recorded.
        }
      }
      throw requestError;
    }
  };
  window.fetch = patchedFetch;
  state.originalFetch = originalFetch;
  state.patchedFetch = patchedFetch;
}

function installXmlHttpRequestListeners(
  state: SupportBrowserDiagnosticState,
  request: XMLHttpRequest,
  requestState: InstrumentedXmlHttpRequestState
) {
  if (requestState.hasInstalledListeners) return;
  requestState.hasInstalledListeners = true;
  for (const errorKind of ["error", "abort", "timeout"] as const) {
    request.addEventListener(errorKind, () => {
      const currentRequestState = state.xmlHttpRequestStateByRequest.get(request);
      if (currentRequestState) currentRequestState.errorKind = `XMLHttpRequest ${errorKind}`;
    });
  }
  request.addEventListener("loadend", () => {
    const currentRequestState = state.xmlHttpRequestStateByRequest.get(request);
    if (
      !currentRequestState ||
      currentRequestState.hasRecordedCompletion ||
      currentRequestState.startedAtIso === null ||
      currentRequestState.startedAtMonotonicMilliseconds === null
    ) {
      return;
    }
    currentRequestState.hasRecordedCompletion = true;
    const sanitizedUrl = resolveSanitizedRequestUrl(currentRequestState.rawUrl);
    if (!sanitizedUrl) return;
    let responseStatus: number | null = null;
    try {
      responseStatus = finiteResponseStatusOrNull(request.status);
    } catch {
      responseStatus = null;
    }
    try {
      recordNetworkEntry(state, {
        timestamp: currentRequestState.startedAtIso,
        method: currentRequestState.method,
        url: sanitizedUrl,
        status: responseStatus,
        duration_ms: Math.max(
          0,
          Math.round(monotonicNowMilliseconds() - currentRequestState.startedAtMonotonicMilliseconds)
        ),
        error_message: currentRequestState.errorKind,
      });
    } catch {
      // An observer failure must not affect the XMLHttpRequest lifecycle.
    }
  });
}

function installXmlHttpRequestCapture(state: SupportBrowserDiagnosticState) {
  if (typeof XMLHttpRequest === "undefined") return;
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const callableOriginalOpen = originalOpen as unknown as (
    this: XMLHttpRequest,
    ...openArguments: unknown[]
  ) => void;

  const patchedOpen = function (this: XMLHttpRequest, ...openArguments: unknown[]) {
    const existingRequestState = state.xmlHttpRequestStateByRequest.get(this);
    const requestState: InstrumentedXmlHttpRequestState = {
      method: normalizeHttpMethod(openArguments[0]),
      rawUrl: String(openArguments[1] ?? ""),
      startedAtIso: null,
      startedAtMonotonicMilliseconds: null,
      errorKind: null,
      hasRecordedCompletion: false,
      hasInstalledListeners: existingRequestState?.hasInstalledListeners ?? false,
    };
    state.xmlHttpRequestStateByRequest.set(this, requestState);
    installXmlHttpRequestListeners(state, this, requestState);
    return callableOriginalOpen.apply(this, openArguments);
  } as XMLHttpRequest["open"];

  const patchedSend = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const requestState = state.xmlHttpRequestStateByRequest.get(this);
    if (requestState) {
      requestState.startedAtIso = new Date().toISOString();
      requestState.startedAtMonotonicMilliseconds = monotonicNowMilliseconds();
      requestState.errorKind = null;
      requestState.hasRecordedCompletion = false;
    }
    try {
      return originalSend.call(this, body);
    } catch (sendError) {
      if (requestState && !requestState.hasRecordedCompletion) {
        requestState.hasRecordedCompletion = true;
        const sanitizedUrl = resolveSanitizedRequestUrl(requestState.rawUrl);
        if (sanitizedUrl && requestState.startedAtIso && requestState.startedAtMonotonicMilliseconds !== null) {
          try {
            recordNetworkEntry(state, {
              timestamp: requestState.startedAtIso,
              method: requestState.method,
              url: sanitizedUrl,
              status: null,
              duration_ms: Math.max(
                0,
                Math.round(monotonicNowMilliseconds() - requestState.startedAtMonotonicMilliseconds)
              ),
              error_message: sanitizeSupportBrowserDiagnosticText(
                sendError instanceof Error ? `${sendError.name}: ${sendError.message}` : String(sendError),
                MAX_NETWORK_ERROR_CHARACTERS
              ),
            });
          } catch {
            // Preserve the original send error when diagnostics fail.
          }
        }
      }
      throw sendError;
    }
  } as XMLHttpRequest["send"];

  state.originalXmlHttpRequestOpen = originalOpen;
  state.originalXmlHttpRequestSend = originalSend;
  state.patchedXmlHttpRequestOpen = patchedOpen;
  state.patchedXmlHttpRequestSend = patchedSend;
  XMLHttpRequest.prototype.open = patchedOpen;
  XMLHttpRequest.prototype.send = patchedSend;
}

function installUnhandledBrowserErrorCapture(state: SupportBrowserDiagnosticState) {
  const windowErrorListener = (event: ErrorEvent) => {
    try {
      recordConsoleEntry(
        state,
        "error",
        event.error instanceof Error ? [event.error] : [event.message],
        "Unhandled browser error:"
      );
    } catch {
      // Diagnostics must not interfere with the browser's error event.
    }
  };
  const unhandledRejectionListener = (event: PromiseRejectionEvent) => {
    try {
      recordConsoleEntry(state, "error", [event.reason], "Unhandled promise rejection:");
    } catch {
      // Diagnostics must not interfere with unhandled-rejection reporting.
    }
  };
  state.windowErrorListener = windowErrorListener;
  state.unhandledRejectionListener = unhandledRejectionListener;
  window.addEventListener("error", windowErrorListener);
  window.addEventListener("unhandledrejection", unhandledRejectionListener);
}

/** Install once near application bootstrap so a later Bug report has history. */
export function initializeSupportBrowserDiagnosticHistory() {
  const state = getOrCreateDiagnosticState();
  if (!state || state.isInstalled) return;
  state.isInstalled = true;
  for (const installCapture of [
    installConsoleCapture,
    installFetchCapture,
    installXmlHttpRequestCapture,
    installUnhandledBrowserErrorCapture,
  ]) {
    try {
      installCapture(state);
    } catch {
      // A browser may lock one global. Keep the app and the other capture paths working.
    }
  }
}

export function readSupportBrowserDiagnosticHistory(): SupportBrowserDiagnosticHistorySnapshot | null {
  const state = readDiagnosticState();
  if (!state?.isInstalled) return null;
  return {
    consoleCaptureActive: Object.keys(state.patchedConsoleMethods).length > 0,
    networkCaptureActive: Boolean(state.patchedFetch || state.patchedXmlHttpRequestOpen),
    consoleEntries: state.consoleEntries.map((entry) => ({ ...entry })),
    networkEntries: state.networkEntries.map((entry) => ({ ...entry })),
  };
}

/** Restore browser globals so focused unit tests never leak instrumentation. */
export function restoreSupportBrowserDiagnosticHistoryForTests() {
  const state = readDiagnosticState();
  if (!state || typeof window === "undefined") return;
  for (const consoleLevel of CAPTURED_CONSOLE_LEVELS) {
    const originalConsoleMethod = state.originalConsoleMethods[consoleLevel];
    const patchedConsoleMethod = state.patchedConsoleMethods[consoleLevel];
    if (originalConsoleMethod && patchedConsoleMethod && console[consoleLevel] === patchedConsoleMethod) {
      console[consoleLevel] = originalConsoleMethod;
    }
  }
  if (state.originalFetch && state.patchedFetch && window.fetch === state.patchedFetch) {
    window.fetch = state.originalFetch;
  }
  if (
    state.originalXmlHttpRequestOpen &&
    state.patchedXmlHttpRequestOpen &&
    XMLHttpRequest.prototype.open === state.patchedXmlHttpRequestOpen
  ) {
    XMLHttpRequest.prototype.open = state.originalXmlHttpRequestOpen;
  }
  if (
    state.originalXmlHttpRequestSend &&
    state.patchedXmlHttpRequestSend &&
    XMLHttpRequest.prototype.send === state.patchedXmlHttpRequestSend
  ) {
    XMLHttpRequest.prototype.send = state.originalXmlHttpRequestSend;
  }
  if (state.windowErrorListener) window.removeEventListener("error", state.windowErrorListener);
  if (state.unhandledRejectionListener) {
    window.removeEventListener("unhandledrejection", state.unhandledRejectionListener);
  }
  delete (window as unknown as Record<string, unknown>)[SUPPORT_DIAGNOSTIC_STATE_PROPERTY];
}
